import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SectionChat } from '@/components/ui/SectionChat';
import { functionsService } from '@/services/functionsService';
import { firestoreService } from '@/services/firestoreService';
import { useCredits } from '@/contexts/CreditsContext';
import { exportAnalysisToPDF } from '@/utils/export';
import { decodeHtmlEntities } from '@/utils/textUtils';
import ReactFlow, { Node, Edge, Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';

interface TranscriptAnalysis {
  summary: string;
  risksAndOpportunities: {
    risks: string[];
    opportunities: string[];
  };
  keyNumbers: {
    revenue?: string;
    earnings?: string;
    guidance?: string;
    other: Record<string, string>;
  };
  strategyAndInitiatives: string[];
  sentiment: {
    overall: 'positive' | 'neutral' | 'negative';
    score: number;
    indicators: string[];
  };
  analystQA: {
    questions: Array<{
      question: string;
      answer: string;
      analyst?: string;
    }>;
  };
  mindmap?: {
    nodes: Array<{ id: string; label: string; type: string }>;
    edges: Array<{ from: string; to: string; label?: string }>;
  };
}

interface Transcript {
  id: string;
  companyId: string;
  companyName: string;
  ticker: string;
  quarter: string;
  date: any;
  text: string;
  sourceUrl?: string;
}

interface PressRelease {
  id: string;
  title: string;
  content?: string;
  text?: string;
  date: any;
  url?: string;
}

// Placeholder stored when real transcript was missing (e.g. after XBRL cleanup)
const TRANSCRIPT_UNAVAILABLE_PLACEHOLDER = '[Transcript unavailable - previously stored filing metadata.]';

function isTranscriptUnavailable(text: string | undefined): boolean {
  if (!text || text.length < 50) return true;
  const t = text.trim();
  if (t === TRANSCRIPT_UNAVAILABLE_PLACEHOLDER) return true;
  if (t.includes('Transcript unavailable') && t.length < 200) return true;
  return false;
}

interface TranscriptViewerProps {
  transcriptId: string;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ transcriptId }) => {
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [analysis, setAnalysis] = useState<TranscriptAnalysis | null>(null);
  const [pressReleases, setPressReleases] = useState<PressRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showRawTranscript, setShowRawTranscript] = useState(false);
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'summary' | 'risks' | 'numbers' | 'strategy' | 'sentiment' | 'qa' | 'mindmap' | 'release' | 'transcript'>('summary');
  const [roDropdownOpen, setRoDropdownOpen] = useState(false);
  const [roDropdownPosition, setRoDropdownPosition] = useState({ top: 0, left: 0 });
  const roTriggerRef = useRef<HTMLButtonElement>(null);
  const roDropdownRef = useRef<HTMLDivElement>(null);
  const { balance, refreshBalance } = useCredits();
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // Position dropdown under the R&O button (for portal)
  const updateRoDropdownPosition = () => {
    if (roTriggerRef.current) {
      const rect = roTriggerRef.current.getBoundingClientRect();
      setRoDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    }
  };

  useEffect(() => {
    if (roDropdownOpen && roTriggerRef.current) {
      updateRoDropdownPosition();
      window.addEventListener('scroll', updateRoDropdownPosition, true);
      window.addEventListener('resize', updateRoDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateRoDropdownPosition, true);
        window.removeEventListener('resize', updateRoDropdownPosition);
      };
    }
  }, [roDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (roTriggerRef.current?.contains(target)) return;
      if (roDropdownRef.current?.contains(target)) return;
      setRoDropdownOpen(false);
    };

    if (roDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [roDropdownOpen]);

  useEffect(() => {
    loadTranscript();
  }, [transcriptId]);

  useEffect(() => {
    setShowRawTranscript(false);
    setTranscriptSearch('');
  }, [transcriptId]);

  const cleanedTranscriptText = useMemo(() => {
    if (!transcript?.text) return '';
    const decoded = decodeHtmlEntities(transcript.text);
    const normalized = decoded
      .replace(/\r\n/g, '\n')
      .replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\uFFFF]/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return normalized;
  }, [transcript?.text]);

  const isLikelyGarbled = useMemo(() => {
    if (!transcript?.text) return false;
    const raw = transcript.text;
    if (raw.length < 400) return false;
    const printable = raw.match(/[\x20-\x7E]/g)?.length || 0;
    const printableRatio = printable / raw.length;
    const words = raw.match(/[A-Za-z]{3,}/g)?.length || 0;
    const wordRatio = words / Math.max(1, raw.length / 5);
    return printableRatio < 0.7 || wordRatio < 0.15;
  }, [transcript?.text]);

  const displayTranscriptText = useMemo(() => {
    if (!transcript) return '';
    const raw = showRawTranscript ? decodeHtmlEntities(transcript.text) : cleanedTranscriptText;
    return raw;
  }, [transcript, showRawTranscript, cleanedTranscriptText]);

  const isUnavailable = useMemo(
    () => isTranscriptUnavailable(transcript?.text),
    [transcript?.text]
  );

  const transcriptWordCount = useMemo(() => {
    if (!displayTranscriptText || isUnavailable) return 0;
    return displayTranscriptText.split(/\s+/).filter(Boolean).length;
  }, [displayTranscriptText, isUnavailable]);

  const renderHighlightedText = (text: string, query: string) => {
    if (!query) return text;
    const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${safeQuery})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-gray-900 px-1 rounded">
          {part}
        </mark>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  const loadTranscript = async () => {
    try {
      setLoading(true);
      const transcriptData = await firestoreService.getDocument<Transcript>('transcripts', transcriptId);
      
      if (!transcriptData) {
        throw new Error('Transcript not found');
      }

      setTranscript(transcriptData);

      const [existingAnalysis, releasesByCompanyId, releasesByTicker] = await Promise.all([
        firestoreService.getDocuments<{ analysis: TranscriptAnalysis }>('transcriptAnalyses', [
          firestoreService.where('transcriptId', '==', transcriptId),
          firestoreService.orderBy('createdAt', 'desc'),
          firestoreService.limit(1),
        ]),
        firestoreService.getDocuments<PressRelease>('pressReleases', [
          firestoreService.where('companyId', '==', transcriptData.companyId),
          firestoreService.orderBy('date', 'desc'),
          firestoreService.limit(10),
        ]).catch(() => []),
        firestoreService.getDocuments<PressRelease>('pressReleases', [
          firestoreService.where('ticker', '==', (transcriptData.ticker || '').toUpperCase()),
          firestoreService.limit(10),
        ]).catch(() => []),
      ]);

      if (existingAnalysis.length > 0 && existingAnalysis[0].analysis) {
        setAnalysis(existingAnalysis[0].analysis);
      }
      const releases = (releasesByCompanyId.length > 0 ? releasesByCompanyId : releasesByTicker)
        .map((r) => ({
          ...r,
          content: r.content || r.text || "",
        }))
        .sort((a, b) => {
          const ad = new Date(a.date?.toDate?.() || a.date).getTime();
          const bd = new Date(b.date?.toDate?.() || b.date).getTime();
          return bd - ad;
        })
        .slice(0, 10);
      setPressReleases(releases);
    } catch (error) {
      console.error('Failed to load transcript:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!transcript || balance < 10) {
      alert('Insufficient credits. You need at least 10 credits to analyze a transcript.');
      return;
    }

    try {
      setAnalyzing(true);
      const result = await functionsService.analyzeTranscript(transcriptId);
      if (result && result.analysis) {
        setAnalysis(result.analysis);
        await refreshBalance();
      } else {
        throw new Error('Invalid response from analysis function');
      }
    } catch (error: any) {
      console.error('Analysis error:', error);
      const errorMessage =
        error?.message ||
        error?.details?.message ||
        (typeof error?.details === 'string' ? error.details : null) ||
        error?.code ||
        'Failed to analyze transcript. Check that the OpenAI API key is set in Firebase (see QUICK_START or README).';
      alert(`Failed to analyze: ${errorMessage}`);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!transcript) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="body-text text-gray-600">Transcript not found</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="h2 text-2xl mb-1">{transcript.companyName}</h2>
            <p className="body-text text-gray-600">
              {transcript.ticker} • {transcript.quarter} • {new Date(transcript.date?.toDate?.() || transcript.date).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            {!analysis && (
              <Button
                variant="primary"
                onClick={handleAnalyze}
                isLoading={analyzing}
                disabled={balance < 10 || isUnavailable}
                title={isUnavailable ? 'Transcript text is unavailable; analysis requires real transcript content.' : undefined}
              >
                {isUnavailable ? 'Analyze (transcript unavailable)' : 'Analyze (10 credits)'}
              </Button>
            )}
            {analysis && (
              <Button
                variant="outline"
                onClick={() => exportAnalysisToPDF(analysis, `${transcript.companyName} - ${transcript.quarter}`)}
              >
                Export PDF
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto overflow-y-visible items-end">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'summary'
                ? 'border-blazerex-green text-blazerex-green font-header-bold'
                : 'border-transparent text-gray-600 hover:text-blazerex-green'
            }`}
          >
            Summary
          </button>
          
          {/* R&O Dropdown - panel rendered in portal so it is not clipped by overflow */}
          <div className="shrink-0">
            <button
              ref={roTriggerRef}
              type="button"
              onClick={() => setRoDropdownOpen(!roDropdownOpen)}
              aria-expanded={roDropdownOpen}
              aria-haspopup="true"
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap flex items-center gap-1 ${
                ['risks', 'numbers', 'strategy', 'sentiment', 'qa'].includes(activeTab)
                  ? 'border-blazerex-green text-blazerex-green font-header-bold'
                  : 'border-transparent text-gray-600 hover:text-blazerex-green'
              }`}
            >
              R&O
              <svg
                className={`w-4 h-4 transition-transform shrink-0 ${roDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {roDropdownOpen &&
              createPortal(
                <div
                  ref={roDropdownRef}
                  className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] min-w-[220px] max-h-[min(50vh,320px)] overflow-y-auto py-1"
                  style={{ top: roDropdownPosition.top, left: roDropdownPosition.left }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('risks');
                      setRoDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors whitespace-nowrap body-text ${
                      activeTab === 'risks' ? 'text-blazerex-green font-medium' : 'text-gray-700'
                    }`}
                  >
                    Risks & Opportunities
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('numbers');
                      setRoDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors whitespace-nowrap body-text ${
                      activeTab === 'numbers' ? 'text-blazerex-green font-medium' : 'text-gray-700'
                    }`}
                  >
                    Key Numbers
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('qa');
                      setRoDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors whitespace-nowrap body-text ${
                      activeTab === 'qa' ? 'text-blazerex-green font-medium' : 'text-gray-700'
                    }`}
                  >
                    Analyst Q&A
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('sentiment');
                      setRoDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors whitespace-nowrap body-text ${
                      activeTab === 'sentiment' ? 'text-blazerex-green font-medium' : 'text-gray-700'
                    }`}
                  >
                    Sentiment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('strategy');
                      setRoDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors whitespace-nowrap body-text ${
                      activeTab === 'strategy' ? 'text-blazerex-green font-medium' : 'text-gray-700'
                    }`}
                  >
                    Strategies
                  </button>
                </div>,
                document.body
              )}
          </div>

          <button
            onClick={() => setActiveTab('mindmap')}
            className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'mindmap'
                ? 'border-blazerex-green text-blazerex-green font-header-bold'
                : 'border-transparent text-gray-600 hover:text-blazerex-green'
            }`}
          >
            Mind Map
          </button>
          
          <button
            onClick={() => setActiveTab('release')}
            className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'release'
                ? 'border-blazerex-green text-blazerex-green font-header-bold'
                : 'border-transparent text-gray-600 hover:text-blazerex-green'
            }`}
          >
            Release
          </button>

          <button
            onClick={() => setActiveTab('transcript')}
            className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'transcript'
                ? 'border-blazerex-green text-blazerex-green font-header-bold'
                : 'border-transparent text-gray-600 hover:text-blazerex-green'
            }`}
          >
            Transcript
          </button>
        </div>
      </Card>

      {!analysis && activeTab !== 'transcript' && activeTab !== 'release' ? (
        <Card>
          <div className="text-center py-12">
            <p className="body-text text-gray-600 mb-4">
              This transcript hasn't been analyzed yet.
            </p>
            <Button variant="primary" onClick={handleAnalyze} disabled={balance < 10}>
              Analyze Now (10 credits)
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {activeTab === 'summary' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Summary</h3>
                <p className="body-text text-gray-700 whitespace-pre-wrap">{analysis.summary}</p>
              </Card>
              <SectionChat
                sectionContent={analysis.summary}
                sectionTitle="this summary"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'risks' && analysis && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-red-600">Risks</h3>
                  <ul className="space-y-2">
                    {analysis.risksAndOpportunities.risks.map((risk, i) => (
                      <li key={i} className="body-text text-gray-700 flex items-start gap-2">
                        <span className="text-red-500 mt-1">•</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card>
                  <h3 className="sub-header text-xl mb-4 text-green-600">Opportunities</h3>
                  <ul className="space-y-2">
                    {analysis.risksAndOpportunities.opportunities.map((opp, i) => (
                      <li key={i} className="body-text text-gray-700 flex items-start gap-2">
                        <span className="text-green-500 mt-1">•</span>
                        <span>{opp}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
              <SectionChat
                sectionContent={[
                  'Risks:',
                  ...analysis.risksAndOpportunities.risks,
                  'Opportunities:',
                  ...analysis.risksAndOpportunities.opportunities,
                ].join('\n')}
                sectionTitle="Risks & Opportunities"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'numbers' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Key Financial Numbers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.keyNumbers.revenue && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="body-text text-sm text-gray-600 mb-1">Revenue</p>
                      <p className="sub-header text-lg">{analysis.keyNumbers.revenue}</p>
                    </div>
                  )}
                  {analysis.keyNumbers.earnings && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="body-text text-sm text-gray-600 mb-1">Earnings</p>
                      <p className="sub-header text-lg">{analysis.keyNumbers.earnings}</p>
                    </div>
                  )}
                  {analysis.keyNumbers.guidance && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="body-text text-sm text-gray-600 mb-1">Guidance</p>
                      <p className="sub-header text-lg">{analysis.keyNumbers.guidance}</p>
                    </div>
                  )}
                  {Object.entries(analysis.keyNumbers.other || {}).map(([key, value]) => (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg">
                      <p className="body-text text-sm text-gray-600 mb-1">{key}</p>
                      <p className="sub-header text-lg">{value}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <SectionChat
                sectionContent={[
                  analysis.keyNumbers.revenue && `Revenue: ${analysis.keyNumbers.revenue}`,
                  analysis.keyNumbers.earnings && `Earnings: ${analysis.keyNumbers.earnings}`,
                  analysis.keyNumbers.guidance && `Guidance: ${analysis.keyNumbers.guidance}`,
                  ...Object.entries(analysis.keyNumbers.other || {}).map(([k, v]) => `${k}: ${v}`),
                ].filter(Boolean).join('\n')}
                sectionTitle="Key Numbers"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'strategy' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Strategy & Initiatives</h3>
                <ul className="space-y-3">
                  {analysis.strategyAndInitiatives.map((strategy, i) => (
                    <li key={i} className="body-text text-gray-700 flex items-start gap-3">
                      <span className="text-blazerex-green mt-1">→</span>
                      <span>{strategy}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <SectionChat
                sectionContent={analysis.strategyAndInitiatives.join('\n')}
                sectionTitle="Strategy & Initiatives"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'sentiment' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Sentiment Analysis</h3>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="body-text">Overall Sentiment</span>
                    <span className={`sub-header capitalize ${
                      analysis.sentiment.overall === 'positive' ? 'text-green-600' :
                      analysis.sentiment.overall === 'negative' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {analysis.sentiment.overall}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        analysis.sentiment.overall === 'positive' ? 'bg-green-600' :
                        analysis.sentiment.overall === 'negative' ? 'bg-red-600' : 'bg-gray-600'
                      }`}
                      style={{ width: `${analysis.sentiment.score}%` }}
                    ></div>
                  </div>
                  <p className="body-text text-sm text-gray-500 mt-1">Score: {analysis.sentiment.score}/100</p>
                </div>
                <div>
                  <p className="body-text text-sm text-gray-600 mb-2">Key Indicators:</p>
                  <ul className="space-y-1">
                    {analysis.sentiment.indicators.map((indicator, i) => (
                      <li key={i} className="body-text text-sm text-gray-700">• {indicator}</li>
                    ))}
                  </ul>
                </div>
              </Card>
              <SectionChat
                sectionContent={[
                  `Overall: ${analysis.sentiment.overall}, Score: ${analysis.sentiment.score}/100`,
                  'Indicators:',
                  ...analysis.sentiment.indicators,
                ].join('\n')}
                sectionTitle="Sentiment"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'qa' && analysis && (
            <>
              <Card>
                <h3 className="sub-header text-xl mb-4">Analyst Q&A</h3>
                <div className="space-y-6">
                  {analysis.analystQA.questions.map((qa, i) => (
                    <div key={i} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="mb-2">
                        {qa.analyst && (
                          <span className="body-text text-xs text-gray-500">Analyst: {qa.analyst}</span>
                        )}
                      </div>
                      <p className="body-text font-medium text-gray-900 mb-2">Q: {qa.question}</p>
                      <p className="body-text text-gray-700">A: {qa.answer}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <SectionChat
                sectionContent={analysis.analystQA.questions
                  .map((qa) => `Q: ${qa.question}\nA: ${qa.answer}`)
                  .join('\n\n')}
                sectionTitle="Analyst Q&A"
                context={{ transcriptId, companyId: transcript?.companyId }}
              />
            </>
          )}

          {activeTab === 'mindmap' && analysis && analysis.mindmap && (
            <Card>
              <h3 className="sub-header text-xl mb-4">Analysis Mindmap</h3>
              <div className="w-full h-[600px] min-h-[400px] border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                <MindmapVisualization mindmap={analysis.mindmap} />
              </div>
            </Card>
          )}

          {activeTab === 'release' && (
            <Card>
              <h3 className="sub-header text-xl mb-4">Press Releases</h3>
              {pressReleases.length === 0 ? (
                <div className="text-center py-12">
                  <p className="body-text text-gray-500">No press releases found for this company.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pressReleases.map((release) => (
                    <div key={release.id} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="sub-header text-lg">{release.title}</h4>
                        <span className="body-text text-sm text-gray-500">
                          {new Date(release.date?.toDate?.() || release.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="body-text text-gray-700 mb-2 line-clamp-3">{release.content}</p>
                      {release.url && (
                        <a
                          href={release.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blazerex-green hover:underline body-text text-sm"
                        >
                          Read more →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {activeTab === 'transcript' && (
            <Card>
              <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="sub-header text-xl">Full Transcript</h3>
                  {!isUnavailable && (
                    <p className="body-text text-sm text-gray-500">
                      {transcriptWordCount.toLocaleString()} words
                      {isLikelyGarbled ? ' • Text looks unparsed' : ''}
                    </p>
                  )}
                  {transcript.sourceUrl && (
                    <a
                      href={transcript.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="body-text text-sm text-blazerex-green hover:underline"
                    >
                      View source filing →
                    </a>
                  )}
                </div>
                {!isUnavailable && (
                  <div className="flex flex-wrap gap-2">
                    <Input
                      type="text"
                      value={transcriptSearch}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTranscriptSearch(e.target.value)}
                      placeholder="Search transcript"
                      className="min-w-[200px]"
                    />
                    <Button
                      variant="outline"
                      onClick={() => setShowRawTranscript((prev) => !prev)}
                    >
                      {showRawTranscript ? 'Show cleaned' : 'Show raw'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(displayTranscriptText);
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                )}
              </div>

              {isUnavailable ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
                  <p className="body-text text-amber-800 font-medium mb-1">Transcript unavailable</p>
                  <p className="body-text text-sm text-amber-700">
                    This record had no real transcript content (it previously stored filing metadata). To see a transcript here, re-run data population for this company so a real earnings transcript can be fetched from the source.
                  </p>
                </div>
              ) : (
                <>
                  {isLikelyGarbled && !showRawTranscript && (
                    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                      This transcript looks like raw filing data or a binary export. We’re showing a cleaned version.
                      If it still looks wrong, switch to “Show raw” or re-run the SEC fetch for this company.
                    </div>
                  )}

                  <div
                    ref={transcriptContainerRef}
                    className="bg-gray-50 p-6 rounded-lg max-h-[28rem] overflow-y-auto"
                  >
                    <div className="body-text text-gray-800 whitespace-pre-wrap max-w-3xl leading-relaxed" style={{ fontFamily: 'inherit' }}>
                      {renderHighlightedText(displayTranscriptText, transcriptSearch)}
                    </div>
                  </div>
                </>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// Mindmap Visualization Component
interface MindmapVisualizationProps {
  mindmap: {
    nodes: Array<{ id: string; label: string; type: string }>;
    edges: Array<{ from: string; to: string; label?: string }>;
  };
}

const MindmapVisualization: React.FC<MindmapVisualizationProps> = ({ mindmap }) => {
  const nodes = useMemo<Node[]>(() => {
    if (!mindmap?.nodes?.length) return [];

    const centerNode = mindmap.nodes.find(n => n.type === 'center') || mindmap.nodes[0];
    const centerId = centerNode.id;
    const nodeMap = new Map<string, Node>();
    const categoryNodes: typeof mindmap.nodes = [];
    const childNodes: typeof mindmap.nodes = [];
    const edges = mindmap.edges || [];

    mindmap.nodes.forEach(node => {
      if (node.id === centerId) {
        categoryNodes.push(node);
      } else {
        const isConnectedToCenter = edges.some(e =>
          (e.from === centerId && e.to === node.id) || (e.to === centerId && e.from === node.id)
        );
        if (isConnectedToCenter) categoryNodes.push(node);
        else childNodes.push(node);
      }
    });

    const cx = 400;
    const cy = 300;
    nodeMap.set(centerId, {
      id: centerId,
      type: 'default',
      position: { x: cx, y: cy },
      data: { label: centerNode.label },
      style: {
        background: '#76B900',
        color: '#000',
        border: '2px solid #5A8A00',
        borderRadius: '8px',
        padding: '10px',
        fontWeight: 'bold',
        fontSize: '14px',
        minWidth: '120px',
        maxWidth: '200px',
      },
    });

    const categoryOnly = categoryNodes.filter(n => n.id !== centerId);
    const categoryCount = categoryOnly.length;
    const radius = 200;
    categoryOnly.forEach((node, index) => {
      const angle = categoryCount > 0 ? (2 * Math.PI * index) / categoryCount : 0;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      const nodeColor = getNodeColor(node.type);
      nodeMap.set(node.id, {
        id: node.id,
        type: 'default',
        position: { x, y },
        data: { label: node.label },
        style: {
          background: nodeColor.background,
          color: nodeColor.text,
          border: `2px solid ${nodeColor.border}`,
          borderRadius: '6px',
          padding: '8px',
          fontSize: '12px',
          minWidth: '80px',
          maxWidth: '180px',
        },
      });
    });

    const childRadius = 120;
    childNodes.forEach((node, index) => {
      const edge = edges.find(e => e.to === node.id || e.from === node.id);
      const parentId = edge ? (edge.from === node.id ? edge.to : edge.from) : centerId;
      const parentNode = nodeMap.get(parentId);
      const baseX = parentNode ? parentNode.position.x : cx;
      const baseY = parentNode ? parentNode.position.y : cy;
      const childAngle = childNodes.length > 0 ? (2 * Math.PI * index) / childNodes.length : 0;
      const x = baseX + childRadius * Math.cos(childAngle);
      const y = baseY + childRadius * Math.sin(childAngle);
      const nodeColor = getNodeColor(node.type);
      nodeMap.set(node.id, {
        id: node.id,
        type: 'default',
        position: { x, y },
        data: { label: node.label },
        style: {
          background: nodeColor.background,
          color: nodeColor.text,
          border: `1px solid ${nodeColor.border}`,
          borderRadius: '4px',
          padding: '6px',
          fontSize: '11px',
          minWidth: '60px',
          maxWidth: '160px',
        },
      });
    });

    return Array.from(nodeMap.values());
  }, [mindmap]);

  const edges = useMemo<Edge[]>(() => {
    if (!mindmap?.edges?.length || nodes.length === 0) return [];
    const nodeIds = new Set(nodes.map(n => n.id));
    return mindmap.edges
      .filter(e => nodeIds.has(e.from) && nodeIds.has(e.to))
      .map(edge => ({
        id: `${edge.from}-${edge.to}`,
        source: edge.from,
        target: edge.to,
        type: 'smoothstep' as const,
        animated: false,
        style: { stroke: '#76B900', strokeWidth: 2 },
        label: edge.label || '',
      }));
  }, [mindmap?.edges, nodes]);

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[300px]">
        <p className="body-text text-gray-600">No mindmap data available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-[300px]">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        attributionPosition="bottom-left"
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

function getNodeColor(type: string): { background: string; text: string; border: string } {
  switch (type) {
    case 'center':
      return { background: '#76B900', text: '#000', border: '#5A8A00' };
    case 'category':
      return { background: '#E8F5E9', text: '#000', border: '#76B900' };
    case 'metric':
      return { background: '#FFF9C4', text: '#000', border: '#F9A825' };
    case 'risk':
      return { background: '#FFEBEE', text: '#000', border: '#E53935' };
    case 'opportunity':
      return { background: '#E8F5E9', text: '#000', border: '#4CAF50' };
    case 'strategy':
      return { background: '#E3F2FD', text: '#000', border: '#2196F3' };
    case 'sentiment':
      return { background: '#F3E5F5', text: '#000', border: '#9C27B0' };
    default:
      return { background: '#F5F5F5', text: '#000', border: '#9E9E9E' };
  }
}

